import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaCode, FaMobile, FaCloud, FaBrain, FaShieldAlt, FaChartLine, 
  FaArrowRight, FaShoppingCart, FaCalendarCheck, FaUsers, 
  FaChalkboardTeacher, FaHospital, FaHotel, FaStore, FaTruck,
  FaClipboardList, FaCreditCard, FaFileInvoice, FaClock
} from 'react-icons/fa';
import './Services.css';

const Services = () => {
  const services = [
    {
      icon: <FaCode />,
      title: 'Web Development',
      description: 'Custom web applications built with modern frameworks like React, Node.js, and more.',
      color: '#0b2b5c'
    },
    {
      icon: <FaMobile />,
      title: 'Mobile Apps',
      description: 'Native and cross-platform mobile applications for iOS and Android.',
      color: '#00b4d8'
    },
    {
      icon: <FaCloud />,
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and deployment services on AWS, Azure, and GCP.',
      color: '#ff6b6b'
    },
    {
      icon: <FaBrain />,
      title: 'AI & Machine Learning',
      description: 'Intelligent solutions powered by cutting-edge AI technology.',
      color: '#9c88ff'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Cybersecurity',
      description: 'Comprehensive security solutions to protect your digital assets.',
      color: '#20bf6b'
    },
    {
      icon: <FaChartLine />,
      title: 'Digital Transformation',
      description: 'End-to-end digital transformation services for modern businesses.',
      color: '#f39c12'
    }
  ];

  const products = [
    {
      icon: <FaFileInvoice />,
      title: 'Billing & Invoicing System',
      description: 'Complete billing solution with automated invoicing, payment tracking, and financial reporting.',
      features: ['Automated Invoicing', 'Payment Processing', 'Tax Management', 'Financial Reports'],
      color: '#6366f1',
      link: '/products/billing-system'
    },
    {
      icon: <FaCalendarCheck />,
      title: 'Booking & Reservation System',
      description: 'Comprehensive booking platform for appointments, reservations, and schedule management.',
      features: ['Real-time Availability', 'Online Payments', 'Email Notifications', 'Calendar Integration'],
      color: '#ec489a',
      link: '/products/booking-system'
    },
    {
      icon: <FaShoppingCart />,
      title: 'E-Commerce Platform',
      description: 'Feature-rich online store solution with inventory management and customer analytics.',
      features: ['Product Management', 'Shopping Cart', 'Payment Gateways', 'Order Tracking'],
      color: '#f59e0b',
      link: '/products/ecommerce-platform'
    },
    {
      icon: <FaUsers />,
      title: 'HR Management System',
      description: 'Complete HR solution for employee management, payroll, attendance, and performance tracking.',
      features: ['Employee Database', 'Payroll Processing', 'Leave Management', 'Performance Reviews'],
      color: '#10b981',
      link: '/products/hr-system'
    },
    {
      icon: <FaChalkboardTeacher />,
      title: 'School Management System',
      description: 'All-in-one platform for educational institutions to manage students, teachers, and academics.',
      features: ['Student Records', 'Fee Management', 'Exam Scheduling', 'Parent Portal'],
      color: '#8b5cf6',
      link: '/products/school-management'
    },
    {
      icon: <FaHospital />,
      title: 'Hospital Management System',
      description: 'Comprehensive healthcare solution for patient management, appointments, and medical records.',
      features: ['Patient Records', 'Appointment Scheduling', 'Pharmacy Management', 'Billing'],
      color: '#ef4444',
      link: '/products/hospital-management'
    },
    {
      icon: <FaHotel />,
      title: 'Hotel Management System',
      description: 'Complete hotel management solution for reservations, room management, and guest services.',
      features: ['Room Booking', 'Check-in/out', 'Guest Management', 'Housekeeping'],
      color: '#3b82f6',
      link: '/products/hotel-management'
    },
    {
      icon: <FaStore />,
      title: 'POS & Retail System',
      description: 'Point of Sale system with inventory management, sales tracking, and customer loyalty.',
      features: ['Sales Processing', 'Inventory Control', 'Customer Management', 'Reports'],
      color: '#14b8a6',
      link: '/products/pos-system'
    },
    {
      icon: <FaTruck />,
      title: 'Logistics Management System',
      description: 'Complete logistics solution for fleet management, route optimization, and delivery tracking.',
      features: ['Fleet Tracking', 'Route Planning', 'Delivery Management', 'Warehouse Control'],
      color: '#f97316',
      link: '/products/logistics-system'
    },
    {
      icon: <FaClipboardList />,
      title: 'Project Management Tool',
      description: 'Comprehensive project management platform for teams to collaborate and track progress.',
      features: ['Task Management', 'Team Collaboration', 'Time Tracking', 'Gantt Charts'],
      color: '#a855f7',
      link: '/products/project-management'
    },
    {
      icon: <FaCreditCard />,
      title: 'Payment Gateway Integration',
      description: 'Secure payment processing solution with multiple payment method support.',
      features: ['Multiple Gateways', 'Secure Transactions', 'Recurring Payments', 'Refund Management'],
      color: '#06b6d4',
      link: '/products/payment-gateway'
    },
    {
      icon: <FaClock />,
      title: 'Attendance & Time Tracking',
      description: 'Employee attendance system with biometric integration and real-time tracking.',
      features: ['Biometric Integration', 'Real-time Tracking', 'Shift Management', 'Overtime Calculation'],
      color: '#64748b',
      link: '/products/attendance-system'
    }
  ];

  return (
    <>
      {/* Services Section */}
      <section className="services" id="services">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <span className="section-subtitle">Our Services</span>
            <h2 className="section-title">
              Comprehensive Software <span className="gradient-text">Solutions</span>
            </h2>
            <p className="section-description">
              We offer a wide range of software development services to help your business thrive in the digital age.
            </p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="service-card"
                whileHover={{ y: -10 }}
              >
                <div className="service-icon" style={{ background: `linear-gradient(135deg, ${service.color}20, ${service.color}40)` }}>
                  <span style={{ color: service.color }}>{service.icon}</span>
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                {/* <Link to={`/services/${service.title.toLowerCase().replace(/\s+/g, '-')}`} className="service-link">
                  Learn More <span className="link-arrow">→</span>
                </Link> */}
                <div className="service-shape" style={{ background: `linear-gradient(135deg, ${service.color}, transparent)` }}></div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="services-cta"
          >
            <Link to="/services" className="btn btn-primary">
              View All Services <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="products-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <span className="section-subtitle">Our Products</span>
            <h2 className="section-title">
              Ready-to-Use <span className="gradient-text">Business Solutions</span>
            </h2>
            <p className="section-description">
              Pre-built software products that can be customized to meet your specific business needs.
            </p>
          </motion.div>

          <div className="products-grid">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="product-card"
                whileHover={{ y: -5 }}
              >
                <div className="product-icon" style={{ background: `linear-gradient(135deg, ${product.color}20, ${product.color}40)` }}>
                  <span style={{ color: product.color }}>{product.icon}</span>
                </div>
                <h3 className="product-title">{product.title}</h3>
                <p className="product-description">{product.description}</p>
                <div className="product-features">
                  {product.features.map((feature, idx) => (
                    <span key={idx} className="feature-tag">✓ {feature}</span>
                  ))}
                </div>
                {/* <Link to={product.link} className="product-link">
                  Learn More <span className="link-arrow">→</span>
                </Link> */}
                <div className="product-shape" style={{ background: `linear-gradient(135deg, ${product.color}, transparent)` }}></div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="products-cta"
          >
            <Link to="/projects" className="btn btn-outline">
              Explore All Projects <FaArrowRight />
            </Link>
            <Link to="/contact" className="btn btn-primary">
              Get Custom Solution <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;